/* eslint class-methods-use-this: 0 */
import { XhrConsts } from 'client/util/Constants';
import { get } from 'lodash';
import axios from 'axios';

const { xCSRFToken, xOrgIdHeader } = XhrConsts;

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

/**
 * The base Api class provides common methods that are used by all Api
 * subclasses for both RESTful and non-RESTful endpoints. It provides an
 * initialize method that is used to ensure that the CSRF token and OrgId are
 * present on all requests as well methods to handle errors and stringify an
 * object of query string parameters for use in a GET request.
 */
export default class Api {
  constructor(csrfUrl = '/csrf', csrfHeader = xCSRFToken, orgIdHeader = xOrgIdHeader) {
    // TODO: We will eventually want to have all Api's using `this.axios`
    // rather than the global `axios` to avoid sharing headers.
    // For now, this local axios instance is only being used in LoginApi.
    this.axios = axios.create({
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    this.csrfUrl = csrfUrl;
    this.csrfHeader = csrfHeader;
    this.orgIdHeader = orgIdHeader;
  }

  initialize() {
    // eslint-disable-next-line no-console
    if (console && console.warn) {
      // eslint-disable-next-line no-console
      console.warn('deprecated API.initialize()');
    }
    return Promise.resolve();
  }

  handleError(reject, mesg, error) {
    if (axios.isCancel(error)) {
      reject(error);
    }

    const apiError = new Error();
    const responseStatus = get(error, 'response.status');
    const responseMessage = get(error, 'response.data.message');
    const errorMesg = get(error, 'response.data.error');
    apiError.status = responseStatus;
    apiError.message = responseMessage || errorMesg || mesg;
    apiError.originalError = error;

    if (typeof reject === 'function') {
      reject(apiError);
    }
    return apiError || Error(mesg);
  }

  getAllItems(url, { filter = '', skip = 0, limit = 100 } = {}, mesg = 'Unable to get all items!') {
    let items = [];

    const getResults = (s, l, nextPageToken, skipToken) => {
      let params;
      // Microsoft 365 import users pagination token
      if (skipToken) {
        params = {
          skip: s,
          limit: l,
          skipToken: `${skipToken}`,
        };
      // Google import users pagination token
      } else if (nextPageToken) {
        params = {
          skip: s,
          limit: l,
          pageToken: `${nextPageToken}`,
        };
      } else {
        params = {
          skip: s,
          limit: l,
        };
      }
      if (filter) params.filter = filter;

      return axios.get(url, { params })
        .then((resp) => {
          let results;
          let totalCount;
          let ret;

          if (!resp || !resp.data || typeof resp.data !== 'object') {
            throw Error(mesg);
          }

          if (Array.isArray(resp.data)) {
            totalCount = parseInt(resp.headers['x-total-count'], 10);
            results = resp.data;
          } else if ((resp.data.nextPageToken || resp.data.nextPageToken === '') && Array.isArray(resp.data.users)) {
            results = resp.data.users;
          } else if ((resp.data.skipToken || resp.data.skipToken === '') && Array.isArray(resp.data.users)) {
            results = resp.data.users;
          } else {
            ({ results, totalCount } = resp.data);
          }

          items = items.concat(results);

          // Defensive move to prevent infinite requests
          // if data from API is wrong
          if (resp
              && resp.config
              && resp.config.params
              && resp.config.params.skip > totalCount) {
            return items;
          }

          if (resp.data.nextPageToken) {
            ret = getResults(s + l, l, resp.data.nextPageToken);
          } else if (resp.data.skipToken) {
            ret = getResults(s + l, l, false, resp.data.skipToken);
          } else if (items.length < totalCount) {
            ret = getResults(s + l, l);
          } else {
            ret = items;
          }
          return ret;
        })
        .catch((error) => {
          const err = !error ? Error(mesg) : error;
          // Using a noop here because you can't use Promise.reject in a catch
          this.handleError(() => {}, mesg, err);
          throw err;
        });
    };

    return getResults(skip, limit);
  }

  getAllItemsCount(url) {
    const config = {
      params: {
        skip: 0,
        limit: 1,
      },
    };

    return axios.get(url, config)
      .then((resp) => {
        if (Array.isArray(resp.data)) {
          return parseInt(resp.headers['x-total-count'], 10);
        }

        if (resp.data && resp.data.totalCount) {
          return resp.data.totalCount;
        }

        return 0;
      });
  }

  /**
   * Stringify an object of query string parameters for use in a GET request.
   * @see
   * https://stackoverflow.com/questions/1714786/query-string-encoding-of-a-javascript-object
   * http://api.jquery.com/jquery.param/
   */
  param(obj, prefix) {
    const str = [];

    Object.keys(obj).forEach((property) => {
      if (Object.prototype.hasOwnProperty.call(obj, property)) {
        const key = prefix ? `${prefix}[${property}]` : property;
        const value = obj[property];

        if (value !== null && typeof value === 'object') {
          // Recurse to process nested objects
          str.push(this.param(value, key));
        } else {
          const typeOfValue = typeof value;

          // Add the key/value pair to the URL if the value is a boolean, number or non-empty string
          if (typeOfValue === 'boolean'
              || typeOfValue === 'number'
              || (typeOfValue === 'string' && value.trim().length !== 0)) {
            str.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
          }
        }
      }
    });

    return str.join('&');
  }
}
